import { useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";

import {
  fmtMoney,
  fmtQty,
  monthLabel,
  useAssignLineItem,
  useChargeTypes,
  useCosLineItems,
  useCosPeriod,
  useMappingChangelog,
  useOverrideLineItem,
  useProductLines,
  useSeedDefaults,
  useUpdateMapping,
  useVendors,
  type AssignRequest,
  type ChargeTypeRead,
  type LineItemRead,
  type LineItemStatus,
} from "../api";

const PAGE = 50;
const EXCLUDED = "__excluded__";

const AMORT_LABEL: Record<string, string> = {
  none: "None (this month)",
  monthly: "Monthly",
  annual_12: "Annual ÷ 12",
  quarterly_3: "Quarterly ÷ 3",
};

/**
 * The mapping workbench: every parsed invoice line (source sheet + line item)
 * and where it lands in the product lines. Classifying a line creates the
 * durable rule that catches the same charge next month; the rules and their
 * audit trail live below the worklist.
 */
export function MappingsView({ period, canMap }: { period: string; canMap: boolean }) {
  const { data: detail } = useCosPeriod(period);
  const exists = detail?.exists ?? false;
  const signedOff = detail?.status === "signed_off";
  const [status, setStatus] = useState<LineItemStatus | "">("unmapped");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const items = useCosLineItems(period, { status, q, limit: PAGE, offset }, exists);
  const { data: productLines } = useProductLines();
  const chargeTypes = useChargeTypes();
  const seed = useSeedDefaults(period);
  const [assigning, setAssigning] = useState<LineItemRead | null>(null);
  const [editingRule, setEditingRule] = useState<ChargeTypeRead | null>(null);

  const lineTitle = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of productLines ?? []) map[p.key] = p.title;
    return map;
  }, [productLines]);

  const columns: DataTableColumn<LineItemRead>[] = [
    { key: "sheet", header: "Source sheet", render: (r) => r.source_sheet ?? "—" },
    {
      key: "description",
      header: "Invoice line item",
      render: (r) => (
        <div className="max-w-[360px]">
          <div className="truncate font-medium" title={r.description ?? undefined}>
            {r.description ?? "—"}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {r.vendor_name ? `${r.vendor_name} · ${r.charge_type_name ?? ""}` : (r.upload_filename ?? "—")}
          </div>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (r) => fmtMoney(r.raw_amount),
    },
    {
      key: "maps_to",
      header: "Maps to",
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1">
          {r.status === "excluded" ? (
            <Badge variant="secondary">Excluded from COGS</Badge>
          ) : r.product_line_key ? (
            <Badge variant="secondary">
              {lineTitle[r.product_line_key] ?? r.product_line_key}
            </Badge>
          ) : r.suggested_line_key ? (
            <span className="text-xs text-muted-foreground">
              suggested: {lineTitle[r.suggested_line_key] ?? r.suggested_line_key}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
          {(r.manual_excluded || r.manual_product_line_key) && (
            <Badge variant="outline">this row only</Badge>
          )}
        </div>
      ),
    },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <KpiTile
          label="Total line items"
          value={fmtQty(items.data?.total_rows ?? null)}
          onClick={() => {
            setStatus("");
            setOffset(0);
          }}
          selected={status === ""}
        />
        <KpiTile
          label="Unmapped"
          value={fmtQty(items.data?.unmapped_rows ?? null)}
          tone={(items.data?.unmapped_rows ?? 0) > 0 ? "warning" : "neutral"}
          onClick={() => {
            setStatus("unmapped");
            setOffset(0);
          }}
          selected={status === "unmapped"}
        />
        <KpiTile
          label="Excluded from COGS"
          value={fmtQty(items.data?.excluded_rows ?? null)}
          onClick={() => {
            setStatus("excluded");
            setOffset(0);
          }}
          selected={status === "excluded"}
        />
      </div>

      {!exists && (
        <Card className="p-5 text-sm text-muted-foreground">
          {monthLabel(period)} hasn&apos;t been opened yet — upload its cost files first.
        </Card>
      )}

      {exists && (
        <DataTable
          columns={columns}
          rows={items.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={items.isLoading}
          search={q}
          onSearchChange={(v) => {
            setQ(v);
            setOffset(0);
          }}
          searchPlaceholder="Search descriptions or sheets…"
          emptyMessage={
            status === "unmapped"
              ? "Nothing unmapped — every line item has a rule. 🎉"
              : "No line items match."
          }
          onRowClick={canMap && !signedOff ? (r) => setAssigning(r) : undefined}
          selectedKey={assigning?.id ?? null}
          pagination={
            items.data && items.data.count > PAGE
              ? { limit: items.data.limit, offset: items.data.offset, count: items.data.count }
              : undefined
          }
          onPageChange={setOffset}
          toolbar={
            canMap && (chargeTypes.data?.length ?? 0) === 0 ? (
              <Button
                variant="outline"
                size="sm"
                disabled={seed.isPending}
                onClick={() =>
                  seed.mutate(undefined, {
                    onSuccess: (r) =>
                      toast.success(
                        `Seeded ${r.rules_created} rules across ${r.vendors_created} vendors`,
                      ),
                    onError: (e) =>
                      toast.error("Seeding failed", { description: (e as Error).message }),
                  })
                }
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                Seed default mappings
              </Button>
            ) : undefined
          }
        />
      )}

      <RulesCard
        chargeTypes={chargeTypes.data ?? []}
        lineTitle={lineTitle}
        canMap={canMap}
        onEdit={(ct) => setEditingRule(ct)}
      />
      <ChangelogCard />

      <AssignDrawer
        period={period}
        item={assigning}
        onClose={() => setAssigning(null)}
        lineTitle={lineTitle}
      />
      <RuleEditDrawer period={period} rule={editingRule} onClose={() => setEditingRule(null)} />
    </div>
  );
}

// ── Assign drawer ─────────────────────────────────────────────────────────────

function AssignDrawer({
  period,
  item,
  onClose,
  lineTitle,
}: {
  period: string;
  item: LineItemRead | null;
  onClose: () => void;
  lineTitle: Record<string, string>;
}) {
  const { data: vendors } = useVendors(item != null);
  const { data: productLines } = useProductLines();
  const assign = useAssignLineItem(period);
  const override = useOverrideLineItem(period);
  const [expanded, setExpanded] = useState(false);

  // Form state is keyed on the item so reopening resets it.
  const [vendorKey, setVendorKey] = useState("other");
  const [ruleName, setRuleName] = useState("");
  const [lineKey, setLineKey] = useState<string>("");
  const [matchKind, setMatchKind] = useState<"sheet" | "exact" | "regex">("exact");
  const [pattern, setPattern] = useState("");
  const [amort, setAmort] = useState<AssignRequest["amortization"]>("none");
  const [sign, setSign] = useState<AssignRequest["sign"]>("charge");
  const [notes, setNotes] = useState("");
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (item != null && seededFor !== item.id) {
    setSeededFor(item.id);
    setVendorKey(item.vendor_raw ?? "other");
    setRuleName((item.description ?? "").slice(0, 80));
    setLineKey(item.product_line_key ?? item.suggested_line_key ?? "");
    setMatchKind("exact");
    setPattern(item.description ?? "");
    setAmort("none");
    setSign((item.raw_amount ?? 0) < 0 ? "credit" : "charge");
    setNotes("");
  }

  const save = () => {
    if (item == null || !ruleName.trim() || !lineKey) return;
    const excluded = lineKey === EXCLUDED;
    assign.mutate(
      {
        itemId: item.id,
        payload: {
          vendor_key: vendorKey,
          canonical_name: ruleName.trim(),
          product_line_key: excluded ? null : lineKey,
          match_kind: matchKind,
          match_pattern: matchKind === "sheet" && !pattern.trim() ? null : pattern,
          amortization: amort,
          sign,
          include_in_cogs: !excluded,
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            excluded
              ? "Excluded from COGS"
              : `Mapped to ${lineTitle[lineKey] ?? lineKey}`,
          );
          onClose();
        },
        onError: (e) => toast.error("Couldn't save mapping", { description: (e as Error).message }),
      },
    );
  };

  return (
    <EntityDrawer
      open={item != null}
      onOpenChange={(open) => !open && onClose()}
      expanded={expanded}
      onExpandedChange={setExpanded}
      title="Classify invoice line"
      subtitle={item?.source_sheet ?? undefined}
    >
      {item != null && (
        <div className="flex flex-col gap-4 p-4">
          <Card className="bg-muted/40 p-3 text-sm">
            <div className="font-medium">{item.description ?? "—"}</div>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{item.source_sheet ?? "—"}</span>
              <span>{fmtMoney(item.raw_amount)}</span>
              {item.qty != null && <span>qty {fmtQty(item.qty)}</span>}
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>Maps to product line</Label>
              <Select value={lineKey} onValueChange={setLineKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a product line…" />
                </SelectTrigger>
                <SelectContent>
                  {(productLines ?? []).map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.title}
                    </SelectItem>
                  ))}
                  <SelectItem value={EXCLUDED}>Excluded from COGS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Vendor</Label>
              <Select value={vendorKey} onValueChange={setVendorKey}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(vendors ?? []).map((v) => (
                    <SelectItem key={v.key} value={v.key}>
                      {v.name}
                    </SelectItem>
                  ))}
                  {!(vendors ?? []).some((v) => v.key === "other") && (
                    <SelectItem value="other">(unclassified)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Rule name</Label>
              <Input value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Match</Label>
              <Select value={matchKind} onValueChange={(v) => setMatchKind(v as typeof matchKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact description</SelectItem>
                  <SelectItem value="sheet">This sheet, contains…</SelectItem>
                  <SelectItem value="regex">Regex</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{matchKind === "sheet" ? "Contains (blank = whole sheet)" : "Pattern"}</Label>
              <Input value={pattern} onChange={(e) => setPattern(e.target.value)} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Amortization</Label>
              <Select value={amort} onValueChange={(v) => setAmort(v as typeof amort)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(AMORT_LABEL).map(([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Sign</Label>
              <Select value={sign} onValueChange={(v) => setSign(v as typeof sign)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="charge">Charge</SelectItem>
                  <SelectItem value="credit">Credit (subtracts)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!ruleName.trim() || !lineKey || assign.isPending} onClick={save}>
              {assign.isPending ? "Saving…" : "Save mapping"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Saving creates a durable rule — the same charge maps itself next month. Every
            change lands in the mapping changelog below.
          </p>

          <div className="rounded-md border border-dashed border-border p-3">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              This row only (no rule)
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              For workbook one-offs — a stale row carried forward, a charge that
              belongs elsewhere just this month. Doesn&apos;t affect other months.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={override.isPending}
                onClick={() =>
                  override.mutate(
                    {
                      itemId: item.id,
                      payload: { product_line_key: null, excluded: true },
                    },
                    {
                      onSuccess: () => {
                        toast.success("Row excluded (this month only)");
                        onClose();
                      },
                      onError: (e) =>
                        toast.error("Couldn't override", { description: (e as Error).message }),
                    },
                  )
                }
              >
                Exclude this row
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={override.isPending || !lineKey || lineKey === EXCLUDED}
                onClick={() =>
                  override.mutate(
                    { itemId: item.id, payload: { product_line_key: lineKey, excluded: false } },
                    {
                      onSuccess: () => {
                        toast.success(
                          `Row moved to ${lineTitle[lineKey] ?? lineKey} (this month only)`,
                        );
                        onClose();
                      },
                      onError: (e) =>
                        toast.error("Couldn't override", { description: (e as Error).message }),
                    },
                  )
                }
              >
                Move only this row to {lineKey && lineKey !== EXCLUDED ? (lineTitle[lineKey] ?? lineKey) : "…"}
              </Button>
              {(item.manual_excluded || item.manual_product_line_key) && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={override.isPending}
                  onClick={() =>
                    override.mutate(
                      { itemId: item.id, payload: { product_line_key: null, excluded: false } },
                      {
                        onSuccess: () => {
                          toast.success("Override cleared — back to the rules");
                          onClose();
                        },
                        onError: (e) =>
                          toast.error("Couldn't clear", { description: (e as Error).message }),
                      },
                    )
                  }
                >
                  Clear override
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </EntityDrawer>
  );
}

// ── Rules + changelog cards ───────────────────────────────────────────────────

function RulesCard({
  chargeTypes,
  lineTitle,
  canMap,
  onEdit,
}: {
  chargeTypes: ChargeTypeRead[];
  lineTitle: Record<string, string>;
  canMap: boolean;
  onEdit: (ct: ChargeTypeRead) => void;
}) {
  const columns: DataTableColumn<ChargeTypeRead>[] = [
    {
      key: "name",
      header: "Rule",
      render: (ct) => (
        <div className="max-w-[300px]">
          <div className="truncate font-medium">{ct.canonical_name}</div>
          <div className="text-xs text-muted-foreground">{ct.vendor_name}</div>
        </div>
      ),
    },
    {
      key: "match",
      header: "Matches",
      render: (ct) => (
        <span className="text-xs text-muted-foreground">
          {ct.match_kind === "sheet"
            ? `${ct.source_sheet ?? "any sheet"}${ct.match_pattern ? ` ∋ "${ct.match_pattern}"` : " (whole sheet)"}`
            : ct.match_kind === "exact"
              ? `= "${ct.match_pattern ?? ""}"`
              : `/${ct.match_pattern ?? ""}/`}
        </span>
      ),
    },
    {
      key: "target",
      header: "Maps to",
      render: (ct) =>
        ct.include_in_cogs === false ? (
          <Badge variant="secondary">Excluded</Badge>
        ) : ct.allocation_method === "computed_split" || ct.allocation_method === "fixed_split" ? (
          <div className="flex flex-wrap gap-1">
            {ct.splits.map((s, i) => (
              <Badge key={i} variant="secondary">
                {lineTitle[s.target_product_line_key ?? ""] ?? s.target_product_line_key}
                {s.fixed_pct != null ? ` ${(s.fixed_pct * 100).toFixed(0)}%` : ""}
              </Badge>
            ))}
            <span className="text-[11px] text-muted-foreground">
              {ct.allocation_method === "computed_split" ? "by cost base" : "fixed split"}
            </span>
          </div>
        ) : ct.product_line_key ? (
          <Badge variant="secondary">{lineTitle[ct.product_line_key] ?? ct.product_line_key}</Badge>
        ) : (
          "—"
        ),
    },
    {
      key: "rules",
      header: "Treatment",
      render: (ct) => (
        <span className="text-xs text-muted-foreground">
          {[
            ct.amortization && ct.amortization !== "none" ? AMORT_LABEL[ct.amortization] : null,
            ct.sign === "credit" ? "credit" : null,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </span>
      ),
    },
  ];
  return (
    <CollapsibleCard
      title={`Mapping rules (${chargeTypes.length})`}
      meta="click a rule to edit its treatment"
      defaultOpen={false}
    >
      <DataTable
        columns={columns}
        rows={chargeTypes}
        rowKey={(ct) => ct.id}
        emptyMessage="No rules yet — seed the defaults or classify a line item."
        onRowClick={canMap ? (ct) => (ct.mapping_id ? onEdit(ct) : undefined) : undefined}
      />
    </CollapsibleCard>
  );
}

function RuleEditDrawer({
  period,
  rule,
  onClose,
}: {
  period: string;
  rule: ChargeTypeRead | null;
  onClose: () => void;
}) {
  const { data: productLines } = useProductLines();
  const update = useUpdateMapping(period);
  const [expanded, setExpanded] = useState(false);
  const [lineKey, setLineKey] = useState("");
  const [amort, setAmort] = useState("none");
  const [sign, setSign] = useState("charge");
  const [reason, setReason] = useState("");
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (rule != null && seededFor !== rule.id) {
    setSeededFor(rule.id);
    setLineKey(rule.include_in_cogs === false ? EXCLUDED : (rule.product_line_key ?? ""));
    setAmort(rule.amortization ?? "none");
    setSign(rule.sign ?? "charge");
    setReason("");
  }

  const save = () => {
    if (rule?.mapping_id == null) return;
    const excluded = lineKey === EXCLUDED;
    update.mutate(
      {
        mappingId: rule.mapping_id,
        payload: {
          product_line_key: excluded ? null : lineKey || null,
          amortization: amort as "none" | "monthly" | "annual_12" | "quarterly_3",
          sign: sign as "charge" | "credit",
          include_in_cogs: !excluded,
          reason: reason.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success("Rule updated");
          onClose();
        },
        onError: (e) => toast.error("Couldn't update rule", { description: (e as Error).message }),
      },
    );
  };

  return (
    <EntityDrawer
      open={rule != null}
      onOpenChange={(open) => !open && onClose()}
      expanded={expanded}
      onExpandedChange={setExpanded}
      title={rule?.canonical_name ?? ""}
      subtitle={rule?.vendor_name ?? undefined}
    >
      {rule != null && (
        <div className="flex flex-col gap-4 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>Maps to product line</Label>
              <Select value={lineKey} onValueChange={setLineKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a product line…" />
                </SelectTrigger>
                <SelectContent>
                  {(productLines ?? []).map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.title}
                    </SelectItem>
                  ))}
                  <SelectItem value={EXCLUDED}>Excluded from COGS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Amortization</Label>
              <Select value={amort} onValueChange={setAmort}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(AMORT_LABEL).map(([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Sign</Label>
              <Select value={sign} onValueChange={setSign}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="charge">Charge</SelectItem>
                  <SelectItem value="credit">Credit (subtracts)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>Reason (for the changelog)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </div>
          {(rule.allocation_method === "computed_split" ||
            rule.allocation_method === "fixed_split") && (
            <p className="text-xs text-muted-foreground">
              This rule fans out across {rule.splits.length} product lines
              {rule.allocation_method === "computed_split"
                ? " proportionally to their direct cost each month."
                : " by fixed percentages."}{" "}
              Split targets are managed in the seed defaults for now.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={update.isPending || !lineKey} onClick={save}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </EntityDrawer>
  );
}

function ChangelogCard() {
  const { data } = useMappingChangelog(50);
  return (
    <CollapsibleCard
      title="Mapping changelog"
      meta="append-only audit of every rule change"
      defaultOpen={false}
    >
      {(data ?? []).length === 0 ? (
        <p className="px-1 py-2 text-sm text-muted-foreground">No changes recorded yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(data ?? []).map((c) => (
            <li key={c.id} className="rounded-md border border-border px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge value={c.action} />
                <span className="font-medium">{c.charge_type_name ?? "—"}</span>
                {c.field && <span className="text-xs text-muted-foreground">{c.field}</span>}
                <span className="ml-auto text-xs text-muted-foreground">
                  {c.changed_by_name ?? "—"} · {new Date(c.created_at).toLocaleString()}
                </span>
              </div>
              {(c.old_value || c.new_value) && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {c.old_value ? `${c.old_value} → ` : ""}
                  {c.new_value ?? ""}
                </div>
              )}
              {c.reason && <div className="mt-0.5 text-xs italic text-muted-foreground">{c.reason}</div>}
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}
